import { Activity } from '@apeiron/common'
import { Planet, PlanetLite } from '@apeiron/planet'
import { Box, Container as MuiContainer, styled } from '@mui/material'
import SaleHistoryList from '@src/components/activities/SaleHistoryList'
import PlanetOgData from '@src/components/ogData/PlanetOgData'
import PlanetOrbitalTrackSection from '@src/components/planet/detail/orbitalTrack/PlanetOrbitalTrackSection'
import { CoreAboutSection } from '@src/components/planet/detail/planetAboutSection/CoreAboutSection'
import { PlanetAboutSection } from '@src/components/planet/detail/planetAboutSection/PlanetAboutSection'
import PlanetApostleSlots from '@src/components/planet/detail/PlanetApostleSlots'
import { PlanetDetailCard } from '@src/components/planet/detail/PlanetDetailCard'
import { PlanetElementSection } from '@src/components/planet/detail/planetElementSection/PlanetElementSection'
import { PlanetListSection } from '@src/components/planet/detail/PlanetListSection'
import PlanetSkillMainSection from '@src/components/planet/detail/planetSkillsSection/PlanetSkillMainSection'
import PlanetStats from '@src/components/planet/detail/PlanetStats'
import SeasonSection from '@src/components/planet/detail/seasonChange/SeasonSection'
import ApeironSectionTitle from '@src/components/share/apeiron/ApeironSectionTitle'
import ApeironSideDetail from '@src/components/share/apeiron/ApeironSideDetail'
import { ActivityRoute } from '@src/constants/activity'
import { OrbitalTrackRenewDetachProvider } from '@src/contexts/orbitalTrack/OrbtialTrackRenewDetachContext'
import { usePlanetTypeContext } from '@src/contexts/planet/PlanetTypeContext'
import { isCore } from '@src/util/apeiron/planet'
import { makeNonEmptyArray } from '@src/util/tools/array'
import { FC, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

const Container = styled(MuiContainer)`
  align-items: center;
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
`

const ColumnContainer = styled(Box)`
  flex-direction: row;
  ${props => props.theme.breakpoints.up('res1280')} {
    display: flex;
  }
  ${props => props.theme.breakpoints.down('res1280')} {
    width: 100%;
  }
`

const ColumnData = styled(Box)`
  ${props => props.theme.breakpoints.up('res1280')} {
    max-width: 600px;
    margin-top: 75px;
  }
  ${props => props.theme.breakpoints.down('res1280')} {
    width: 100%;
  }
`

const StyledPlanetOrbitalTrackSection = styled(PlanetOrbitalTrackSection)`
  ${props => props.theme.breakpoints.up('res1280')} {
    width: 78%;
  }
  ${props => props.theme.breakpoints.down('res1280')} {
    width: 360px;
    margin: auto;
  }
`

const ApostleSlots = styled(PlanetApostleSlots)`
  padding-top: 10px;
  max-width: 360px;
  ${props => props.theme.breakpoints.up('res1280')} {
    width: 78%;
  }
  ${props => props.theme.breakpoints.down('res1280')} {
    margin: 0px auto 24px auto;
    width: 100%;
  }
`

const PlanetDetailView: FC<Props> = props => {
  const {
    className,
    isBookmarked = false,
    isEditable = false,
    planet,
    saleHistories,
    shouldShowRelic,
    shouldShowSkill = false,
    shouldShowStats = false,
    onBookmarkChange,
    onNameUpdate,
    onPlanetClick,
    renderMarketSection,
    renderPlanetUrl
  } = props

  if (planet.planetType == null) {
    throw new Error('planet.planetType is empty')
  }

  const { t } = useTranslation()

  const dataContext = usePlanetTypeContext()

  const planetType = dataContext.getPlanetTypeData(planet.planetType)

  const parents = makeNonEmptyArray(planet.parents)

  const children = makeNonEmptyArray(planet.children)

  return (
    <Container maxWidth='res1280' className={className}>
      <PlanetOgData planet={planet} />
      <ColumnContainer>
        <ApeironSideDetail
          cardView={
            <>
              <PlanetDetailCard
                isBookmarked={isBookmarked}
                isEditable={isEditable}
                planet={planet}
                shouldShowRelic={shouldShowRelic}
                onBookmarkChange={onBookmarkChange}
                onNameUpdate={onNameUpdate}
              />
              <ApostleSlots planet={planet} />
              <OrbitalTrackRenewDetachProvider>
                <StyledPlanetOrbitalTrackSection
                  attachedPlanetId={planet.planetID}
                />
              </OrbitalTrackRenewDetachProvider>
            </>
          }
        />
        <ColumnData>
          {renderMarketSection && renderMarketSection(planet)}
          <SeasonSection planet={planet} />
          <ApeironSectionTitle title={t('planet.detail.about.title')} />
          {isCore(planet) ? (
            <CoreAboutSection planet={planet} />
          ) : (
            <PlanetAboutSection planet={planet} />
          )}

          <ApeironSectionTitle title={t('planet.detail.element.title')} />
          <PlanetElementSection
            planet={planet}
            src={planetType ? planetType.planetTypeImage : undefined}
          />

          {!isCore(planet) && (
            <>
              {shouldShowStats && (
                <>
                  <ApeironSectionTitle title={t('planet.detail.stats.title')} />
                  <PlanetStats planet={planet} />
                </>
              )}
              {shouldShowSkill && <PlanetSkillMainSection planet={planet} />}
            </>
          )}

          {parents.length > 0 && (
            <>
              <ApeironSectionTitle title={t('planet.detail.parents.title')} />
              <PlanetListSection
                planets={parents}
                renderPlanetUrl={renderPlanetUrl}
                onPlanetClick={onPlanetClick}
              />
            </>
          )}

          {!isCore(planet) && children.length > 0 && (
            <>
              <ApeironSectionTitle title={t('planet.detail.children.title')} />
              <PlanetListSection
                planets={children}
                renderPlanetUrl={renderPlanetUrl}
                onPlanetClick={onPlanetClick}
              />
            </>
          )}

          {saleHistories && saleHistories.length > 0 && (
            <>
              <ApeironSectionTitle title={t('common.title.sale_history')} />
              <SaleHistoryList
                histories={saleHistories}
                route={ActivityRoute.Planet}
              />
            </>
          )}
        </ColumnData>
      </ColumnContainer>
    </Container>
  )
}

type Props = {
  className?: string
  isBookmarked?: boolean
  isEditable?: boolean
  planet: Planet
  saleHistories?: Activity[]
  shouldShowRelic?: boolean
  shouldShowSkill?: boolean
  shouldShowStats?: boolean
  onBookmarkChange?: (bookmarked: boolean) => void
  onNameUpdate: (name: string) => void
  onPlanetClick?: (planet: PlanetLite) => void
  renderMarketSection?: (planet: Planet) => ReactNode
  renderPlanetUrl?: (planet: PlanetLite) => string
}

export default PlanetDetailView
